import { isValidBasePrice } from '@acme/b2b/pricing';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ACMEListResponse, LooseAutocomplete, TODO, UIBModalService } from '@b2b/types/core';
import { AutorenewalType, MembershipPrice } from '@b2b/types/membership';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IScope, ITimeoutService } from 'angular';

interface MembershipOfferingsCreateDetailsModel {
  name: string;
  subtitle: string;
  description: string;
  duration: number;
  endTime: string | null;
  startTime: string;
  priceList: TODO;
  priceLists: Array<{ prices: MembershipPrice[] }>;
  benefits: TODO[];
  rules: TODO;
  categoryInfo: TODO;
  autorenewalType: AutorenewalType | null;
}

MembershipOfferingsCreateDetailsController.$inject = [
  '$scope',
  '$state',
  '$uibModal',
  '$timeout',
  'ApiService',
  'CalendarService',
  'HelpersService',
  'RulesEngineService',
  'MembershipOfferingService',
  'TenantService',
  'ThemesHelperService',
];

function MembershipOfferingsCreateDetailsController(
  $scope: IScope & { membershipOfferingsCreateDetails: TODO },
  $state: StateService,
  $uibModal: UIBModalService,
  $timeout: ITimeoutService,
  ApiService: ApiService,
  CalendarService: CalendarService,
  HelpersService: TODO,
  RulesEngineService: TODO,
  MembershipOfferingService: TODO,
  TenantService: TODO,
  ThemesHelperService: ThemesHelperService
) {
  const ctrl = {
    personTypes: [] as {
      id: string;
      name: string;
      description: string;
      active: true;
      type: LooseAutocomplete<'Membership'>;
      displayOrder: number;
    }[],
    autorenewalTypes: [
      { label: 'Off', value: 'none' },
      { label: 'Optional', value: 'optional' },
      { label: 'Required', value: 'required' },
    ],

    model: {
      name: '',
      subtitle: '',
      description: '',
      duration: 12,
      endTime: '',
      startTime: '',
      priceList: {},
      priceLists: [{ prices: [] }],
      benefits: [],
      rules: {},
      categoryInfo: {},
      autorenewalType: null,
    } as MembershipOfferingsCreateDetailsModel,

    endTime: new Date(),
    startTime: new Date(),

    descriptionMax: 300,
    descriptionRemaining: 300,

    duration: {
      years: 1,
      months: 0,
    },

    indefinite: false,
    isSubmitted: false,
    isLoaded: false,

    canDisableAutorenew: true,
    enableSalesTax: false,

    init: function () {
      ctrl.enableSalesTax = ThemesHelperService.getFeatureFlag('enableSalesTax');

      ctrl.getPersonTypes();

      if ($state.params.offeringId) {
        ctrl.getOfferingPurchasedlist();
      }

      if ($state.params.categoryId) {
        ctrl.getCategory();
      }

      $scope.$on('MembershipOfferingsCreateController::Save', ctrl.onMembershipOfferingsCreateControllerSave);
      $scope.$on('MembershipOfferingsCreateController::Update', ctrl.onMembershipOfferingsCreateControllerUpdate);
    },

    getCategory: function () {
      ApiService.membership.categories
        .detail($state.params.categoryId)
        .then((response) => ctrl.success.getCategory(response))
        .catch((response) => ctrl.error.getCategory(response));
    },

    getOfferingPurchasedlist: function () {
      const props = {
        levelId: $state.params.categoryId,
        offeringId: $state.params.offeringId,
        isDraftId: 'true',
      };

      ApiService.membership.categories.detail(props.levelId).then((response) => {
        props.isDraftId = response.data.payload.lastPublishedAt ? 'true' : 'false';
        ApiService.membership.offerings.summaries(props).then((res) => {
          if (res.data.payload.list.length) {
            ctrl.canDisableAutorenew = false;
          }
        });
      });
    },

    hasValidPrices: function () {
      const pricePoints = ctrl.model.priceLists?.[0]?.prices ?? [];

      return (
        ctrl.isSubmitted && pricePoints.length > 0 && pricePoints.every((pricePoint) => Number(pricePoint.price) >= 0)
      );
    },

    hasValidBasePrices: function () {
      const priceLists = ctrl.model.priceLists ?? [];

      return (
        ctrl.isSubmitted &&
        priceLists.every((priceList) => {
          const pricePoints = priceList.prices ?? [];

          return pricePoints.every((item) => {
            return isValidBasePrice({ basePrice: item.basePrice ?? null, price: item.price });
          });
        })
      );
    },

    hasValidPricePoint: function () {
      const pricePoints = ctrl.model.priceLists?.[0]?.prices ?? [];

      return pricePoints[0]?.personType != null;
    },

    checkDescription: function () {
      if (ctrl.model.description) {
        ctrl.model.description = ctrl.model.description.replace(/[\s\n]+/g, ' ');
        const len = HelpersService.getTextLength(ctrl.model.description);

        if (len > ctrl.descriptionMax) {
          ctrl.model.description = ctrl.model.description.substr(0, ctrl.descriptionMax);
        }
      } else {
        ctrl.model.description = '';
      }

      ctrl.descriptionRemaining = ctrl.descriptionMax - HelpersService.getTextLength(ctrl.model.description);
    },

    updateDuration: function () {
      ctrl.model.duration = MembershipOfferingService.durationToMonths(ctrl.duration);
      MembershipOfferingService.details.duration = MembershipOfferingService.durationToMonths(ctrl.duration);
    },

    isValidDuration: function () {
      let r = true;
      const duration = parseInt(ctrl.model.duration as TODO, 10);

      if (ctrl.isSubmitted) {
        r = !isNaN(duration) && duration > 0;
      }

      return r;
    },

    durationError: function () {
      $scope.$emit('MembershipOffering::Error', {
        tab: 'details',
        message: 'Duration must be greater than 0.',
      });
    },

    /**
     * Backend returns an error when the person type is missing
     * and our model gets it from the selected price point.
     */
    pricePointError: function () {
      $scope.$emit('MembershipOffering::Error', {
        tab: 'details',
        message: 'Please select a price point.',
      });
    },

    pricesError: function () {
      $scope.$emit('MembershipOffering::Error', {
        tab: 'details',
        message: 'A price of at least $0 is required.',
      });
    },

    basePriceError: function () {
      $scope.$emit('MembershipOffering::Error', {
        tab: 'details',
        message: 'Please enter a valid base price',
      });
    },

    isIndividualAndFamily: function () {
      return ctrl.model.categoryInfo.membershipLevelGroup === 'individualAndFamily';
    },

    isRecurringPaymentEnabled: function () {
      return TenantService.isRecurringPaymentEnabled();
    },

    runFrontendValidations: function () {
      if (!ctrl.hasValidPricePoint()) {
        ctrl.pricePointError();
        return false;
      }

      if (!ctrl.hasValidPrices()) {
        ctrl.pricesError();
        return false;
      }

      if (!ctrl.hasValidBasePrices()) {
        ctrl.basePriceError();
        return false;
      }

      if (!ctrl.isValidDuration()) {
        ctrl.durationError();
        return false;
      }

      return true;
    },

    complete: function (flags) {
      ctrl.model.startTime = CalendarService.toISO8601(ctrl.startTime);
      ctrl.model.endTime = ctrl.indefinite ? null : CalendarService.toISO8601(ctrl.endTime);
      ctrl.model.duration = MembershipOfferingService.durationToMonths(ctrl.duration);

      if (!ctrl.runFrontendValidations()) {
        return;
      }

      if (flags) {
        const model = angular.extend({ categoryId: $state.params.categoryId }, ctrl.model);

        if (flags.create) {
          ApiService.membership.offerings
            .create(model)
            .then((response) => ctrl.success.create(response))
            .catch((response) => ctrl.error.create(response));
        } else if (flags.publish || flags.save) {
          ApiService.membership.offerings
            .update(model)
            .then((response) => ctrl.success.update(response))
            .catch((response) => ctrl.error.update(response));
        }
      }
    },

    getPersonTypes: function () {
      const props = HelpersService.getPaginator({ sortField: 'displayOrder', sortDirection: 'asc' });
      props.types = 'membership';
      props.inactive = false;

      ApiService.price.types
        .list(props)
        .then((response) => ctrl.success.getPersonTypes(response))
        .catch((response) => ctrl.error.getPersonTypes(response));
    },

    success: {
      init: function (response) {},

      generic: function () {
        $scope.$emit('MembershipOffering::Success', {
          tab: 'details',
          message: 'Successfully saved offering details',
        });
      },

      create: function (response) {
        $state.go('application.membership.offerings.edit', {
          categoryId: $state.params.categoryId,
          offeringId: response.data.payload.id,
        });
      },

      update: function (response) {
        ctrl.success.generic();
      },

      getCategory: function (response) {
        ctrl.model.categoryInfo = response.data.payload;

        /**
         * Defaults autorenewalType to 'none' if unset
         */
        if (ctrl.model.autorenewalType == null) {
          ctrl.model.autorenewalType = 'none';
        }
      },

      getPersonTypes: function (response: {
        data: ACMEListResponse<{
          id: string;
          name: string;
          description: string;
          active: true;
          type: LooseAutocomplete<'Membership'>;
          displayOrder: number;
        }>;
      }) {
        ctrl.personTypes = response.data.payload.list;
      },
    },

    error: {
      init: function (response) {},

      generic: function (response) {
        $scope.$emit('MembershipOffering::Error', {
          tab: 'details',
          message: response.data.error,
        });
      },

      create: function (response) {
        ctrl.error.generic(response);
      },

      update: function (response) {
        ctrl.error.generic(response);
      },

      getCategory: function (response) {
        ctrl.error.generic(response);
      },

      getPersonTypes: function (response) {
        ctrl.error.generic(response);
      },
    },

    onMembershipOfferingsCreateControllerSave: function (e, data) {
      ctrl.isSubmitted = true;

      if (!ctrl.runFrontendValidations()) {
        return;
      }

      ctrl.success.generic();
      MembershipOfferingService.details = angular.copy(ctrl.model);
      MembershipOfferingService.ready.details = true;
    },

    onMembershipOfferingsCreateControllerUpdate: function (e, data) {
      angular.extend(ctrl.model, data.offering);

      ctrl.duration = MembershipOfferingService.monthsToDuration(ctrl.model.duration);

      MembershipOfferingService.details = angular.copy(ctrl.model);

      ctrl.updateDuration();

      MembershipOfferingService.loaded.details = true;
    },
  };

  $scope.membershipOfferingsCreateDetails = ctrl;
  ctrl.init();
}

export const MembershipOfferingsCreateDetailsControllerModule = angular
  .module('acme.view.application.membership.offerings.create.details.controllers', [
    uiRouter,
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.calendar',
    'acme.common.service.helpers',
    'acme.common.service.rulesEngine',
    'acme.common.service.membership.offering',
    'acme.common.service.tenant',
    'acme.view.application.membership.offerings.create.details.modals.autorenew.controllers',
  ])
  .controller('MembershipOfferingsCreateDetailsController', MembershipOfferingsCreateDetailsController).name;
